import gsap from 'gsap';

export default class LazyLoader {
  constructor() {
    if (LazyLoader.instance) {
      return LazyLoader.instance;
    }

    this.observer = null;
    this.initialized = false;
    LazyLoader.instance = this;
    this.init();
  }

  static getInstance() {
    if (!LazyLoader.instance) {
      LazyLoader.instance = new LazyLoader();
    }
    return LazyLoader.instance;
  }

  init() {
    if (this.initialized) {
      return;
    }

    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.loadImage(entry.target);
            this.observer.unobserve(entry.target);
          }
        });
      }, {
        rootMargin: '200px 0px',
        threshold: 0.01
      });

      this.observeImages();
    } else {
      this.loadAllImages();
    }

    this.initialized = true;
  }

  observeImages() {
    const lazyImages = document.querySelectorAll('img.lazy-load[data-src]');
    lazyImages.forEach(img => {
      this.observer.observe(img);
    });
  }

  loadImage(img) {
    const src = img.getAttribute('data-src');
    if (!src) return;

    // Check if image is already loading or loaded
    if (img.classList.contains('lazy-loading') || img.classList.contains('lazy-loaded')) {
      return;
    }

    // Mark as loading to prevent duplicate requests
    img.classList.add('lazy-loading');

    const imageLoader = new Image();
    
    imageLoader.onload = () => {
      const isThumbnail = img.closest('.gt-episodes-slider, .modal-thumbnails-slider, .listen-follow');
      const isSpeaker = img.closest('.gt-speakers');
      
      if (isThumbnail) {
        img.src = src;
        img.removeAttribute('data-src');
        img.classList.remove('lazy-load', 'lazy-loading');
        img.classList.add('lazy-loaded');
      } else {
        gsap.set(img, { opacity: 0 });
        img.src = src;
        img.removeAttribute('data-src');
        img.classList.remove('lazy-load', 'lazy-loading');
        img.classList.add('lazy-loaded');
        
        gsap.to(img, {
          opacity: 1,
          duration: 0.3,
          ease: "power2.out"
        });
      }

      // Check if this was a speaker image and if all speakers are now loaded
      if (isSpeaker) {
        setTimeout(() => {
          this.checkAndRemoveSpeakersFromObserver();
        }, 50);
      }
    };

    imageLoader.onerror = () => {
      img.removeAttribute('data-src');
      img.classList.remove('lazy-load', 'lazy-loading');
      img.classList.add('lazy-error');
    };

    imageLoader.src = src;
  }

  loadAllImages() {
    const lazyImages = document.querySelectorAll('img.lazy-load[data-src]');
    lazyImages.forEach(img => {
      this.loadImage(img);
    });
  }

  observeNewImages() {
    if (this.observer) {
      const newLazyImages = document.querySelectorAll('img.lazy-load[data-src]');
      newLazyImages.forEach(img => {
        this.observer.observe(img);
      });
    }
  }

  loadVisibleSlideImages() {
    const visibleSlides = document.querySelectorAll('.swiper-slide-active img.lazy-load[data-src], .swiper-slide-next img.lazy-load[data-src], .swiper-slide-prev img.lazy-load[data-src]');
    visibleSlides.forEach(img => {
      this.loadImage(img);
    });
    
    // Also load the next 2 slides ahead
    const nextSlides = document.querySelectorAll('.gt-episodes-slider .swiper-slide-active ~ .swiper-slide:nth-child(-n+3) img.lazy-load[data-src]');
    nextSlides.forEach(img => {
      this.loadImage(img);
    });
  }

  loadModalThumbnailImages() {
    const modalVisibleSlides = document.querySelectorAll('.modal-thumbnails-slider .swiper-slide-active img.lazy-load[data-src], .modal-thumbnails-slider .swiper-slide-next img.lazy-load[data-src], .modal-thumbnails-slider .swiper-slide-prev img.lazy-load[data-src]');
    modalVisibleSlides.forEach(img => {
      this.loadImage(img);
    });
  }

  loadInitialSlides(count = 5) {
    const firstSlideImages = document.querySelectorAll(`.gt-episodes-slider .swiper-slide:nth-child(-n+${count}) img.lazy-load[data-src]`);
    firstSlideImages.forEach(img => {
      this.loadImage(img);
    });
  }

  loadVisibleSpeakerImages() {
    const speakerImages = document.querySelectorAll('.gt-speakers img.lazy-load[data-src]');
    speakerImages.forEach(img => {
      const speakerRow = img.closest('.gt-speaker-row');
      if (speakerRow) {
        const rect = speakerRow.getBoundingClientRect();
        const isVisible = rect.top < window.innerHeight + 500 && rect.bottom > -500;
        if (isVisible) {
          this.loadImage(img);
        }
      }
    });
  }

  loadAllVisibleSlides() {
    const slider = document.querySelector('.gt-episodes-slider .swiper');
    if (!slider || !slider.swiper) return;
    
    const activeIndex = slider.swiper.activeIndex;
    const slidesPerView = slider.swiper.params.slidesPerView;
    const totalSlides = slider.swiper.slides.length;
    
    // Load current visible slides plus 2 more on each side
    const startIndex = Math.max(0, activeIndex - 2);
    const endIndex = Math.min(totalSlides - 1, activeIndex + Math.ceil(slidesPerView) + 2);
    
    for (let i = startIndex; i <= endIndex; i++) {
      const slide = slider.swiper.slides[i];
      if (slide) {
        const img = slide.querySelector('img.lazy-load[data-src]');
        if (img) {
          this.loadImage(img);
        }
      }
    }
  }

  loadAllSliderImages() {
    const sliderImages = document.querySelectorAll('.gt-episodes-slider img.lazy-load[data-src], .modal-thumbnails-slider img.lazy-load[data-src]');
    sliderImages.forEach(img => {
      this.loadImage(img);
    });
  }

  loadAllSpeakerImages() {
    const speakerImages = document.querySelectorAll('.gt-speakers img.lazy-load[data-src]');
    speakerImages.forEach(img => {
      this.loadImage(img);
    });
    
    // Check if all speakers are loaded and remove from observer
    setTimeout(() => {
      this.checkAndRemoveSpeakersFromObserver();
    }, 100);
  }

  checkAndRemoveSpeakersFromObserver() {
    const remainingSpeakerImages = document.querySelectorAll('.gt-speakers img.lazy-load[data-src]');
    
    if (remainingSpeakerImages.length === 0 && this.observer) {
      // All speakers loaded, remove all speaker images from observer
      const allSpeakerImages = document.querySelectorAll('.gt-speakers img');
      allSpeakerImages.forEach(img => {
        this.observer.unobserve(img);
      });
    }
  }

  destroy() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    
    this.initialized = false;
    LazyLoader.instance = null;
  }
} 